import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { cn } from '@/lib/utils'

export interface SidebarItem {
  label: string
  to: string
  icon?: ReactNode
  end?: boolean
  badge?: ReactNode
  category?: string
}

export interface SidebarProps {
  brand?: ReactNode
  items: SidebarItem[]
  footer?: ReactNode
  className?: string
  backToStore?: boolean
}

function Sidebar({ brand, items, footer, className }: SidebarProps) {
  // Group items by category if available
  const hasCategories = items.some((item) => item.category)

  return (
    <div className={cn('flex h-full flex-col justify-between p-4 overflow-y-auto minimal-red-scrollbar', className)}>
      <div className="space-y-5">
        {brand ? <div className="px-2 pt-1">{brand}</div> : null}

        <nav aria-label="Sidebar navigation" className="flex flex-1 flex-col space-y-4">
          {hasCategories ? (
            // Render with categorized headers
            Array.from(new Set(items.map((i) => i.category || 'General'))).map((categoryName) => {
              const categoryItems = items.filter((i) => (i.category || 'General') === categoryName)
              return (
                <div key={categoryName} className="space-y-1">
                  <div className="px-3 pb-1 text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground/70">
                    {categoryName}
                  </div>
                  {categoryItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'group flex items-center justify-between rounded-xl px-3.5 py-2.5 text-xs font-semibold transition-all duration-150',
                          isActive
                            ? 'bg-[#CF2F2F]/10 text-[#CF2F2F] font-bold border-r-2 border-[#CF2F2F]'
                            : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground',
                        )
                      }
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {item.icon ? (
                          <span
                            aria-hidden
                            className="shrink-0 transition-transform group-hover:scale-105 [&>svg]:size-4.5"
                          >
                            {item.icon}
                          </span>
                        ) : null}
                        <span className="truncate">{item.label}</span>
                      </div>
                      {item.badge ? <span>{item.badge}</span> : null}
                    </NavLink>
                  ))}
                </div>
              )
            })
          ) : (
            // Sequential list fallback
            <div className="space-y-1">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center justify-between rounded-xl px-3.5 py-2.5 text-xs font-semibold transition-all duration-150',
                      isActive
                        ? 'bg-[#CF2F2F]/10 text-[#CF2F2F] font-bold border-r-2 border-[#CF2F2F]'
                        : 'text-foreground/80 hover:bg-accent/60 hover:text-foreground',
                    )
                  }
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {item.icon ? (
                      <span aria-hidden className="shrink-0 transition-transform group-hover:scale-105 [&>svg]:size-4.5">
                        {item.icon}
                      </span>
                    ) : null}
                    <span className="truncate">{item.label}</span>
                  </div>
                  {item.badge ? <span>{item.badge}</span> : null}
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </div>

      {footer ? <div className="pt-4 mt-auto border-t border-border/60">{footer}</div> : null}
    </div>
  )
}

export { Sidebar }